import { Table, TableBody } from "@mui/material";
import React, { createContext, useContext, useEffect, useRef } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as ReactWindowList } from "react-window";
import { mergeRefs } from "../../../Utils/Refs";
import { gridStyles } from "../../GridStyles";
import { useArrowRowFocus } from "../../utils/useArrowRowFocus";
import { GridLoadingSpinner } from "../GridLoadingSpinner";
import DataGridHeader from "./DataGridHeader";
import { DataRowRenderer } from "./DataRowRenderer";
DataGridTable.defaultProps = {
    disableDiagonalScrolling: false,
    fixedColumnCount: 1
};
const ReactTableContext = createContext(undefined);
export default function DataGridTable(props) {
    const { allowMoveColumns, allowPagination, disableDiagonalScrolling, data, dataIsLoading, fixedColumnCount, fixedHeadersElement, getTableProps, getTableBodyProps, headerGroups, page, rowHeight, rows, setColumnOrder, state, visibleColumns } = props;
    //References used to sync horizontal scroll between the body and header. This is only used for iOS and Android devices
    const headerRef = useRef(null);
    const bodyRef = useRef(null);
    const rowsContainerRef = useRef(null);
    const [focusedRowId, onRowIdFocus] = useArrowRowFocus(rowsContainerRef);
    //Set focused row to be undefined when the data changes as there is no guarantee that the previously selected row will still be in the grid
    useEffect(() => {
        onRowIdFocus(undefined);
    }, [data]);
    if (disableDiagonalScrolling) {
        return (React.createElement(React.Fragment, null,
            React.createElement(DataGridHeader, { ...{ allowMoveColumns, bodyRef, fixedColumnCount, fixedHeadersElement, headerRef, state, headerGroups, setColumnOrder, visibleColumns } }),
            React.createElement("div", { style: { position: "relative", display: "flex", height: "100%" } },
                React.createElement(AutoSizer, null, ({ height, width }) => {
                    return (React.createElement(ReactTableContext.Provider, { value: {
                            rowsContainerRef,
                            dataIsLoading,
                            allowMoveColumns,
                            bodyRef,
                            getTableProps,
                            fixedColumnCount,
                            fixedHeadersElement,
                            headerRef,
                            state,
                            headerGroups,
                            setColumnOrder,
                            visibleColumns,
                            width
                        } },
                        React.createElement(ReactWindowList, { className: "data-grid-virtualized-list", height: height, itemCount: allowPagination ? state.pageSize : rows.length, itemData: {
                                page,
                                rows,
                                focusedRowId,
                                onRowIdFocus,
                                allowPagination
                            }, itemSize: rowHeight, innerElementType: ReactTableNoDiagonalScroll, width: width, style: {
                                //This returns a ReactWindowList with only vertical scroll enabled to disallow diagonal scrolling by having the scroll for each direction on separate divs
                                overflow: "hidden auto"
                            }, ...getTableBodyProps() }, DataRowRenderer)));
                }))));
    }
    return (React.createElement(AutoSizer, null, ({ height, width }) => {
        return (React.createElement(ReactTableContext.Provider, { value: {
                rowsContainerRef,
                dataIsLoading,
                allowMoveColumns,
                bodyRef,
                getTableProps,
                fixedColumnCount,
                fixedHeadersElement,
                headerRef,
                state,
                headerGroups,
                setColumnOrder,
                visibleColumns,
                width
            } },
            React.createElement(ReactWindowList, { className: "data-grid-virtualized-list", height: height, itemData: {
                    page,
                    rows,
                    focusedRowId,
                    onRowIdFocus,
                    allowPagination
                }, itemCount: allowPagination ? state.pageSize : rows.length, itemSize: rowHeight, innerElementType: ReactTable, width: width, ...getTableBodyProps() }, DataRowRenderer)));
    }));
}
// #region Renders a table with a header
const ReactTableInner = ({ children, style, ...rest }, ref) => {
    const props = useContext(ReactTableContext);
    const styles = gridStyles();
    if (props) {
        const { rowsContainerRef, dataIsLoading, allowMoveColumns, bodyRef, getTableProps, fixedColumnCount, fixedHeadersElement, headerRef, state, headerGroups, setColumnOrder, visibleColumns } = props;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return (React.createElement(Table, { component: "div", ...getTableProps(), className: `table data-grid`, sx: { ...styles.table, ...style }, ...rest, ref: ref },
            React.createElement(DataGridHeader, { ...{ allowMoveColumns, bodyRef, fixedColumnCount, fixedHeadersElement, headerRef, state, headerGroups, setColumnOrder, visibleColumns } }),
            React.createElement(TableBody, { component: "div", style: { display: "block", height: "100%", width: "100%" }, className: "data-grid-body" },
                React.createElement("div", { style: { position: "relative", display: "flex", width: "100%" }, ref: rowsContainerRef, tabIndex: 0, onFocus: (e) => {
                        //Focus the first row when body is focused on
                        // This was written prior to adding @typescript-eslint/consistent-type-assertions
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        const firstRow = e.currentTarget?.firstElementChild;
                        firstRow && firstRow.focus({ preventScroll: true });
                    } }, dataIsLoading ? React.createElement(GridLoadingSpinner, null) : children))));
    }
    return React.createElement(React.Fragment, null);
};
const ReactTable = React.forwardRef(ReactTableInner);
//#endregion
// #region Renders a component without diagonal scrolling. Used for iOS and Android only. This has to be memoized as the header is not rendered within.
const ReactTableNoDiagonalScrollInner = ({ children, style, ...rest }, ref) => {
    const props = useContext(ReactTableContext);
    if (props) {
        const { rowsContainerRef, dataIsLoading, bodyRef, getTableProps, headerRef, fixedColumnCount, fixedHeadersElement } = props;
        // This calculates the width for the table body and rows (fixed header width + non-fixed total columns width).
        // Fixed header width is used instead of total visible columns width as we need to include width for the RowPrefix/checkbox
        // This need to be calculated against visible columns as the width will change when columns are hidden
        const dataGridBodyWidth = Math.max(props.visibleColumns.reduce((acc, current, index) => {
            if (index >= fixedColumnCount) {
                const width = props.state.headerWidths[current.id];
                return acc + width;
            }
            return acc;
        }, 0) + (fixedHeadersElement?.getBoundingClientRect().width ?? 0), props.width);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return (React.createElement("div", { ...rest, ...getTableProps(), ref: mergeRefs(bodyRef, ref), className: "data-grid", 
            //Only allow horizontal scroll as the parent has vertical scroll, disabling diagonal scrolling
            style: { ...style, overflow: "auto hidden", ...(dataIsLoading ? { height: "100%" } : {}) }, onScroll: (e) => {
                //This makes sure the scroll event does not bubble up to the parent vertical scroll
                e.stopPropagation();
                if (headerRef.current) {
                    headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
            } },
            React.createElement("div", { role: "rowgroup", style: { position: "relative", display: "block", height: "inherit", width: dataGridBodyWidth }, className: "data-grid-body", tabIndex: 0, ref: rowsContainerRef, onFocus: (e) => {
                    //Focus the first row when body is focused on
                    // This was written prior to adding @typescript-eslint/consistent-type-assertions
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const firstRow = e.currentTarget?.firstElementChild;
                    firstRow && firstRow.focus({ preventScroll: true });
                } }, dataIsLoading ? React.createElement(GridLoadingSpinner, null) : children)));
    }
    return React.createElement(React.Fragment, null);
};
const ReactTableNoDiagonalScroll = React.forwardRef(ReactTableNoDiagonalScrollInner);
//#endregion
